import { ArrowTriangleIcon, CurecallBadge, ECurecallBadgeColor } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { twMerge } from 'src/utils/tw-merge.util'
import type { TDashboardPatientMedEvent } from '../dashboard.type'
import { useDashboard } from '../dashboard.provider'
import { getFullName } from 'src/utils/string-formatter.util'

export type TPatientMedEventDropdownProps = {
  label: string
  patientMedEventList: TDashboardPatientMedEvent[]
}

const PatientMedEventDropdown = ({ label, patientMedEventList }: TPatientMedEventDropdownProps) => {
  const { t } = useTranslation()
  const { selectedPatientMedEvent, setSelectedPatientMedEvent } = useDashboard()
  const [isDropdownOpened, setIsDropdownOpened] = useState<boolean>(false)

  const onDropdownToggle = (event: React.MouseEvent<HTMLDetailsElement>) => {
    event.preventDefault()
    setIsDropdownOpened((prev) => !prev)
  }

  const onClickOnPatientMedEvent = (patientMedEvent: TDashboardPatientMedEvent) => {
    setSelectedPatientMedEvent(patientMedEvent)
  }

  useEffect(() => {
    const isPatientSelectedInList = patientMedEventList.some(
      (patientMedEvent) => patientMedEvent.id === selectedPatientMedEvent?.id,
    )
    if (isPatientSelectedInList) setIsDropdownOpened(true)
  }, [patientMedEventList, selectedPatientMedEvent?.id])

  return (
    <details open={isDropdownOpened}>
      <summary
        className="c-sticky c-top-0 c-px-4 c-flex c-items-center c-justify-between c-border-b c-border-b-grey-200 c-shadow-[0px_-1px_0px_0px_#D9DDE5] hover:c-bg-grey-100 c-cursor-pointer c-bg-white"
        onClick={onDropdownToggle}
      >
        <span className="c-py-2 c-truncate c-font-nexa c-font-black">{label}</span>
        <ArrowTriangleIcon className="c-h-4 c-w-4" position={isDropdownOpened ? 'UP' : 'DOWN'} />
      </summary>
      <ul className="c-p-0 c-list-none c-my-0">
        {patientMedEventList.map((patientMedEvent) => {
          if (!patientMedEvent.Patient) return null
          const isPatientMedEventSelected = patientMedEvent.id === selectedPatientMedEvent?.id
          const isPatientMedEventReviewed = patientMedEvent.status === 'REVIEWED'
          return (
            <li
              key={`${patientMedEvent.id}-${patientMedEvent.Patient.lastname}-${patientMedEvent.date}`}
              onClick={() => {
                onClickOnPatientMedEvent(patientMedEvent)
              }}
              className={twMerge(
                'c-flex c-items-center c-gap-4 c-justify-between c-border-b c-border-b-grey-200 c-cursor-pointer hover:c-bg-secondary-100 c-px-4 c-h-[38px] c-font-thin',

                isPatientMedEventSelected &&
                  'c-px-4 c-h-[70px] c-text-secondary-800 c-font-bold c-bg-secondary-100 c-border-r-4 c-border-r-secondary-800',

                isPatientMedEventReviewed && 'hover:c-bg-green-100',

                isPatientMedEventSelected &&
                  isPatientMedEventReviewed &&
                  'c-bg-green-100 c-text-grey-700 c-border-r-green-700',
              )}
            >
              <span className="c-truncate">{getFullName(patientMedEvent.Patient)}</span>{' '}
              {isPatientMedEventReviewed && (
                <CurecallBadge color={ECurecallBadgeColor.GREEN}>
                  {t('components.patient-med-event-overview.reviewed')}
                </CurecallBadge>
              )}
            </li>
          )
        })}
      </ul>
    </details>
  )
}

export default PatientMedEventDropdown
